import { VERSION } from "./version.generated.js";

export const DEFAULT_BASE_URL = "https://foreloop.com/api/storyloop";
// The package's own built asset via CDN; hosts can pass scriptUrl to serve
// their vendored copy instead.
export const DEFAULT_SCRIPT_URL = "https://cdn.jsdelivr.net/npm/@foreloop/feedback@" + VERSION + "/dist/foreloop-feedback.js";
// The main Storyloop API still recognizes this legacy provenance value.
export const SOURCE_TYPE = "storyloop_feedback_widget";

export interface FeedforwardRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface FeedforwardAnnotation {
  type: "element" | "text";
  selector: string;
  label: string;
  text?: string;
  context?: string;
  note?: string;
  rects: FeedforwardRect[];
}

export interface FeedforwardSignalOptions {
  comment: string;
  project?: string;
  url?: string;
  includeQuery?: boolean;
  pageTitle?: string;
  viewport?: string;
  userAgent?: string;
  selectedText?: string;
  productArea?: string;
  category?: string;
  screenshotRef?: string;
  screenshotNote?: string;
  annotations?: FeedforwardAnnotation[];
  evidenceRefs?: string[];
  sourceId?: string;
  reporterName?: string;
  severity?: "none" | "low" | "medium" | "high" | "critical" | (string & {});
  sensitivity?:
    | "public"
    | "internal"
    | "confidential"
    | "customer_pii"
    | "secret"
    | (string & {});
}

export type FeedbackSignalOptions = FeedforwardSignalOptions;

export interface StoryloopFeedforwardPayload {
  kind: "user_feedback";
  title: string;
  body_markdown: string;
  source_type: typeof SOURCE_TYPE;
  source_id: string;
  reporter_name: string | null;
  severity: string;
  sensitivity: string;
  state: "new";
  evidence_refs: string[];
  project_subject_id?: string;
}

export type StoryloopFeedbackPayload = StoryloopFeedforwardPayload;

export function createFeedforwardSignalPayload(
  options: FeedforwardSignalOptions,
): StoryloopFeedforwardPayload {
  const comment = stringValue(options?.comment).trim();
  if (!comment) throw new Error("Storyloop Feedforward comment is required.");

  const pageUrl = sanitizedUrl(options?.url, Boolean(options?.includeQuery));
  const pageTitle = stringValue(options?.pageTitle) || "Not captured";
  const viewport = stringValue(options?.viewport) || defaultViewport();
  const userAgent = stringValue(options?.userAgent) || defaultUserAgent();
  const selectedText = stringValue(options?.selectedText) || "Not captured";
  const productArea = stringValue(options?.productArea) || "Not captured";
  const category = stringValue(options?.category) || "Not captured";
  const screenshotRef = stringValue(options?.screenshotRef);
  const screenshotNote = stringValue(options?.screenshotNote);
  const annotations = annotationMarkdown(options?.annotations);
  const evidenceRefs = uniqueStrings([
    ...arrayValue(options?.evidenceRefs),
    ...(screenshotRef ? [screenshotRef] : [pageUrl]),
  ]);
  const project = stringValue(options?.project).trim();

  return {
    kind: "user_feedback",
    title: titleFromComment(comment),
    body_markdown: [
      "## Raw feedforward",
      comment,
      "",
      "## Page context",
      `- URL: ${pageUrl}`,
      `- Page title: ${pageTitle}`,
      `- Viewport: ${viewport}`,
      `- User agent: ${userAgent}`,
      `- Selected text: ${selectedText}`,
      `- Screenshot: ${screenshotRef || screenshotNote || "Not captured."}`,
      ...annotations,
      "",
      "## Widget context",
      `- Source: ${SOURCE_TYPE}`,
      `- Version: ${VERSION}`,
      `- Product area: ${productArea}`,
      `- Category: ${category}`,
    ].join("\n"),
    source_type: SOURCE_TYPE,
    source_id: stringValue(options?.sourceId) || uniqueSourceId(),
    reporter_name: stringValue(options?.reporterName) || null,
    severity: stringValue(options?.severity) || "none",
    sensitivity: stringValue(options?.sensitivity) || "customer_pii",
    state: "new",
    evidence_refs: evidenceRefs,
    ...(project ? { project_subject_id: project } : {}),
  };
}

function annotationMarkdown(value: unknown): string[] {
  if (!Array.isArray(value) || value.length === 0) return [];
  const lines = ["", "## Annotations"];
  value.slice(0, 20).forEach((raw, index) => {
    if (!raw || typeof raw !== "object") return;
    const annotation = raw as Partial<FeedforwardAnnotation>;
    const type = annotation.type === "text" ? "Selected text" : "Element";
    lines.push("", `### ${index + 1}. ${type}: ${inline(annotation.label, "Target")}`);
    lines.push(`- Selector: \`${inline(annotation.selector, "Not captured", 500).replace(/`/g, "\\`")}\``);
    if (annotation.text) lines.push(`- Text: ${inline(annotation.text, "", 500)}`);
    if (annotation.context) lines.push(`- Context: ${inline(annotation.context, "", 700)}`);
    const rects = Array.isArray(annotation.rects) ? annotation.rects.slice(0, 12) : [];
    if (rects.length > 0) {
      lines.push(`- Position: ${rects.map(formatRect).join("; ")}`);
    }
    if (annotation.note) lines.push(`- Note: ${inline(annotation.note, "", 1000)}`);
  });
  return lines;
}

function formatRect(value: FeedforwardRect): string {
  return ["x", "y", "width", "height"]
    .map((key) => `${key}=${Math.round(numberValue(value?.[key as keyof FeedforwardRect]))}`)
    .join(", ");
}

function inline(value: unknown, fallback: string, limit = 300): string {
  const normalized = stringValue(value).replace(/\s+/g, " ").trim();
  if (!normalized) return fallback;
  return normalized.length > limit ? `${normalized.slice(0, limit - 3)}...` : normalized;
}

function numberValue(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

export function sanitizedUrl(value?: string, includeQuery = false): string {
  const raw =
    stringValue(value) ||
    (typeof window !== "undefined" && window.location ? window.location.href : "");
  if (!raw) return "Not captured";
  try {
    const url = new URL(raw);
    if (!includeQuery) {
      url.search = "";
      url.hash = "";
    }
    return url.toString();
  } catch {
    if (includeQuery) return raw;
    return raw.split(/[?#]/, 1)[0] || "Not captured";
  }
}

export function titleFromComment(comment: string): string {
  const firstLine = comment
    .split(/\r?\n/)
    .map((line) => line.trim())
    .find(Boolean);
  if (!firstLine) return "Feedforward";
  return firstLine.length > 96 ? `${firstLine.slice(0, 93)}...` : firstLine;
}

export function uniqueSourceId(): string {
  if (globalThis.crypto && typeof globalThis.crypto.randomUUID === "function") {
    return `feedforward-widget:${globalThis.crypto.randomUUID()}`;
  }
  return `feedforward-widget:${Date.now()}:${Math.random().toString(36).slice(2)}`;
}

/** @deprecated Use createFeedforwardSignalPayload. */
export const createFeedbackSignalPayload = createFeedforwardSignalPayload;

export function uniqueStrings(values: unknown[]): string[] {
  return [...new Set(values.map(stringValue).map((value) => value.trim()).filter(Boolean))];
}

function defaultViewport(): string {
  if (typeof window === "undefined") return "Not captured";
  return `${window.innerWidth}x${window.innerHeight}`;
}

function defaultUserAgent(): string {
  if (typeof navigator === "undefined") return "Not captured";
  return navigator.userAgent || "Not captured";
}

function stringValue(value: unknown): string {
  return value === null || typeof value === "undefined" ? "" : String(value);
}

function arrayValue(value: unknown): string[] {
  return Array.isArray(value) ? value.map(stringValue).filter(Boolean) : [];
}
